import type { CharacterSummary } from '../types';
import { classColor } from '../lib/wow';

interface Props {
  owner: string;        // display label (or "Other")
  ownerKey: string;     // raw owner value (empty string for legacy/unknown)
  chars: CharacterSummary[];
  onClick: () => void;
}

function pretty(classFile: string): string {
  return classFile.charAt(0) + classFile.slice(1).toLowerCase();
}

function timeAgo(ms: number): string {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/**
 * Big title card representing one account. Shows the owner's name, char
 * count, a "highest character" highlight (level + class colour), and the
 * timestamp of the most-recently-updated character on the account. Click
 * to dive into the account's character list.
 */
export function AccountTitleCard({ owner, ownerKey, chars, onClick }: Props) {
  const sorted = [...chars].sort((a, b) => (b.level - a.level) || (b.updatedAt - a.updatedAt));
  const highest = sorted[0];
  const mostRecent = [...chars].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  const color = highest ? classColor(highest.classFile) : '#cccccc';

  // Class breakdown for the small chip row.
  const classCounts = new Map<string, number>();
  for (const c of chars) {
    classCounts.set(c.classFile, (classCounts.get(c.classFile) ?? 0) + 1);
  }
  const topClasses = Array.from(classCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const isLegacy = ownerKey === '';

  return (
    <button
      type="button"
      className={['account-card', isLegacy ? 'is-legacy' : ''].join(' ').trim()}
      onClick={onClick}
      style={{ '--account-accent': color } as React.CSSProperties}
      title={isLegacy
        ? 'Characters uploaded with the legacy shared token (no owner attribution).'
        : `Open ${owner}'s characters`}
    >
      <div className="account-card-head">
        <div className="account-card-name">{owner}</div>
        <div className="account-card-count">{chars.length} char{chars.length === 1 ? '' : 's'}</div>
      </div>

      {highest && (
        <div className="account-card-highest" style={{ color }}>
          {highest.name} · <span className="muted">L{highest.level} {pretty(highest.classFile)}</span>
        </div>
      )}

      <div className="account-card-class-chips">
        {topClasses.map(([cls, n]) => (
          <span
            key={cls}
            className="class-chip"
            style={{ color: classColor(cls), borderColor: classColor(cls) + '55' }}
            title={`${n} ${pretty(cls)}${n === 1 ? '' : 's'}`}
          >
            {pretty(cls)} <span className="class-chip-count">{n}</span>
          </span>
        ))}
      </div>

      <div className="account-card-footer">
        <span className="muted">Last activity: {mostRecent ? timeAgo(mostRecent.updatedAt) : '—'}</span>
        <span className="account-card-arrow">→</span>
      </div>
    </button>
  );
}
